import { getAllowedCountrySelectOptions } from "../../utils/general";
import { encodeTextForApi } from "../../utils/base64Utils";
import { sanitizeInAppNotificationHtml } from "../../utils/inAppNotificationUtils";
import {
  notificationValidations,
  PUSH_MAX_CTAS,
  PUSH_SELECT_ALL_COUNTRIES,
  PUSH_TYPE_CATEGORY_MAP,
  DEFAULT_PUSH_TARGET_SCREEN,
  PUSH_TARGET_SCREEN_PREFIX,
} from "./notificationConstant";

export function stripPushTargetScreenPrefix(value = "") {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (trimmed.toLowerCase().startsWith(PUSH_TARGET_SCREEN_PREFIX)) {
    return trimmed.slice(PUSH_TARGET_SCREEN_PREFIX.length);
  }
  return trimmed;
}

export function withPushTargetScreenPrefix(path = "") {
  const stripped = stripPushTargetScreenPrefix(path);
  if (!stripped) return "";
  return `${PUSH_TARGET_SCREEN_PREFIX}${stripped}`;
}

export function resolvePushTargetScreen(value) {
  const full = withPushTargetScreenPrefix(value);
  return full || DEFAULT_PUSH_TARGET_SCREEN;
}

export function csvToArray(str, delimiter = ",") {
  if (!str || typeof str !== "string" || !str.includes("\n")) {
    return [];
  }
  const headers = str.slice(0, str.indexOf("\n")).split(delimiter);
  const rows = str.slice(str.indexOf("\n") + 1).split("\n");
  if (rows && rows.length > 0 && rows[rows.length - 1] === "") {
    rows.splice(-1);
  }
  if (!rows || rows.length === 0) {
    return [];
  }
  return rows.map(function (row) {
    const values = row.split(delimiter);
    return headers.reduce(function (object, header, index) {
      object[header] = values[index];
      return object;
    }, {});
  });
}

function cleanCsvCell(value = "") {
  return String(value)
    .replace(/^\uFEFF/, "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(/^["']+|["']+$/g, "")
    .trim();
}

function extractUserIdFromCell(value = "") {
  const cleaned = cleanCsvCell(value);
  if (!cleaned) return "";
  const match = cleaned.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
  );
  return match ? match[0] : "";
}

export function parseCsvUserIdsFromText(textData = "") {
  const userIds = [];
  const lines = String(textData || "")
    .replace(/^\uFEFF/, "")
    .split(/\r\n|\n|\r/);

  for (let i = 0; i < lines.length; i += 1) {
    const cell = (lines[i].split(/[,;\t]/)[0] || "");
    const userId = extractUserIdFromCell(cell);
    if (userId) {
      userIds.push(userId);
    }
  }

  return userIds;
}

export function parseCsvUserIdsFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve([]);
      return;
    }
    const reader = new FileReader();
    reader.onload = function (event) {
      resolve(parseCsvUserIdsFromText(event.target.result));
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read CSV file"));
    reader.readAsText(file);
  });
}

export function stripHtmlToPlainText(html = "") {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function getNotificationMessageForDisplay(notification = {}) {
  const message = notification?.message;
  if (!message || typeof message !== "object") return {};

  if (message.payload != null) {
    return message.payload;
  }

  const emailMessage = {};
  if (message.subject != null && String(message.subject).trim() !== "") {
    emailMessage.subject = message.subject;
  }
  if (message.text != null && String(message.text).trim() !== "") {
    emailMessage.text = message.text;
  }
  if (message.html != null && String(message.html).trim() !== "") {
    emailMessage.html = message.html;
  }
  return emailMessage;
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function getNotificationProcessCounts(details = {}) {
  const tps = details?.status?.taskProcessStatus ?? {};
  const nps = details?.status?.notificationProcessStatus ?? {};

  const successCount =
    toCount(tps.notificationCount) ||
    toCount(nps.notificationSentCount) ||
    toCount(nps.userSentCount);

  const failureCount =
    toCount(tps.userFailedCount) +
    toCount(nps.failedUserSentCount) +
    toCount(nps.tokenNotFoundUserCount);

  return {
    successCount,
    failureCount,
    totalCount: successCount + failureCount,
  };
}

export function toNotificationExpiryEndOfDay(date) {
  if (!date) return null;
  const expiry = new Date(date);
  expiry.setHours(23, 59, 59, 0);
  return expiry.getTime();
}

export function isNotificationExpiryInFuture(expiryDateMs) {
  return Boolean(expiryDateMs && expiryDateMs > Date.now());
}

export function getNotificationExpiryUnixSeconds(expiryDateMs) {
  if (!expiryDateMs) return null;
  return Math.floor(expiryDateMs / 1000);
}

export function getPushCountryCodes(allowedCountries = []) {
  return getAllowedCountrySelectOptions(allowedCountries, {
    includeNone: false,
  }).map((option) => option.value);
}

export function normalizePushCountrySelection(nextValue = [], allCountryCodes = []) {
  const selected = (Array.isArray(nextValue) ? nextValue : []).filter(
    (value) => value && value !== "None"
  );
  const codes = Array.isArray(allCountryCodes) ? allCountryCodes : [];

  if (selected.includes(PUSH_SELECT_ALL_COUNTRIES)) {
    const withoutAll = selected.filter(
      (value) => value !== PUSH_SELECT_ALL_COUNTRIES
    );
    const allSelected =
      codes.length > 0 && codes.every((code) => withoutAll.includes(code));
    return allSelected ? [] : [...codes];
  }

  return selected.filter((value) => codes.includes(value));
}

export function cleanPushCtas(ctas = []) {
  const labelMax = notificationValidations.push.ctaLabel.maxLength;
  const targetMax = notificationValidations.push.ctaTarget.maxLength;
  return (Array.isArray(ctas) ? ctas : [])
    .filter((cta) => cta?.label?.trim() && cta?.target?.trim())
    .slice(0, PUSH_MAX_CTAS)
    .map((cta, index) => ({
      action: "Open",
      id: `button${index + 1}`,
      text: cta.label.trim().slice(0, labelMax),
      targetScreen: cta.target.trim().slice(0, targetMax),
    }));
}

export function buildPushMessagePayload(form = {}, expiryDateMs) {
  const descriptionHtml = sanitizeInAppNotificationHtml(form.description || "");
  const imageUrl = (form.image || "").trim();
  const title = sanitizeInAppNotificationHtml(form.title || "").trim()
    || (form.title || "").trim();
  const targetScreen = resolvePushTargetScreen(form.targetScreen);

  const payload = {
    type: (form.type || "").trim(),
    category: (form.category || "").trim(),
    title,
    description: encodeTextForApi(descriptionHtml),
    targetScreen,
  };

  if (imageUrl) {
    payload.imageUrl = imageUrl;
  }

  const ts = getNotificationExpiryUnixSeconds(expiryDateMs);
  if (ts != null) {
    payload.ts = String(ts);
  }

  const priority = (form.priority || "").trim();
  if (priority) {
    payload.priority = priority;
  }

  const cleanedCtas = cleanPushCtas(form.ctas);
  if (cleanedCtas.length > 0) {
    payload.ctas = JSON.stringify(cleanedCtas);
  }

  return payload;
}

export function validatePushMessageForm(form = {}) {
  if (!(form.type || "").trim()) return "Please select Type";
  if (!(form.category || "").trim()) return "Please select Category";
  const allowedCategories = PUSH_TYPE_CATEGORY_MAP[form.type] || [];
  if (!allowedCategories.includes(form.category)) {
    return "Please select a valid Category for the selected Type";
  }
  if (!(form.title || "").trim()) return "Push notification title cannot be empty";

  const description = form.description || "";
  const hasText = Boolean(stripHtmlToPlainText(description));
  if (!hasText) {
    return "Push notification description cannot be empty";
  }

  const ctas = Array.isArray(form.ctas) ? form.ctas : [];
  if (ctas.length > PUSH_MAX_CTAS) {
    return `A maximum of ${PUSH_MAX_CTAS} CTAs is allowed`;
  }
  for (let i = 0; i < ctas.length; i += 1) {
    const label = ctas[i]?.label?.trim() || "";
    const target = ctas[i]?.target?.trim() || "";
    if ((label && !target) || (!label && target)) {
      return `CTA ${i + 1} requires both Text and Target Screen`;
    }
  }
  return null;
}

export function getOptionLabel(options = [], value, emptyLabel = "") {
  if (!value) return emptyLabel;
  return options.find((option) => option.value === value)?.label || value;
}
